// React-подобные утилиты для работы с CSElement
import { CSElement } from '../core/CSElement';
import { useState, useEffect, useCallback, useMemo } from 'react';

import { 
  UseElementOptions, 
  UseElementResult,
  UseCSElementOptions,
  UseCSElementResult
} from '../types/react-interfaces';

/**
 * React хук для управления одним CSElement
 */
export function useElement(options: UseElementOptions = {}): UseElementResult {
  const { name, data, parent } = options;
  
  const [element, setElement] = useState<CSElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  
  // Инициализация элемента
  useEffect(() => {
    const initializeElement = async () => {
      try {
        setLoading(true);
        setError(null);
        
        let newElement: CSElement | null = null;
        
        if (parent && name) {
          const existing = parent.getElement(name);
          if (existing && existing instanceof CSElement) {
            newElement = existing;
          } else {
            newElement = await parent.addElement(name) as CSElement;
          }
        } else if (name) {
          newElement = new CSElement(name);
        } else {
          const autoName = `element_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          newElement = new CSElement(autoName);
        }

        if (data && newElement) {
          for (const [key, value] of Object.entries(data)) {
            await newElement.setData(key, value);
          }
        }

        setElement(newElement);
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    initializeElement();
  }, [name, parent]);

  const update = useCallback(async (newData: Record<string, any>) => {
    if (!element) return;
    
    try {
      setError(null);
      for (const [key, value] of Object.entries(newData)) {
        await element.setData(key, value);
      }
      setElement(prevElement => prevElement);
    } catch (err) {
      setError(err as Error);
    }
  }, [element]);

  const remove = useCallback(async () => {
    if (!element) return;
    
    try {
      setError(null);
      if (element.mainOwner && element.mainOwner instanceof CSElement) {
        await element.mainOwner.removeElement(element);
      }
      element.destroy();
      setElement(null);
    } catch (err) {
      setError(err as Error);
    }
  }, [element]);

  const save = useCallback(async () => {
    if (!element) return;
    
    try {
      setError(null);
      await element.save();
    } catch (err) {
      setError(err as Error);
    }
  }, [element]);

  return {
    element,
    loading,
    error,
    update,
    remove,
    save
  };
}

/**
 * Хук для получения экземпляра CSElement по ID.
 */
export function useCSElementById(id: string): CSElement | null {
  const [element, setElement] = useState<CSElement | null>(() => CSElement.getElementById(id));

  useEffect(() => {
    setElement(CSElement.getElementById(id));
  }, [id]);

  return element;
}

/**
 * Хук для выполнения запроса к элементу.
 */
export function useQuery(root: CSElement | null, selector: string): CSElement[] {
  return useMemo(() => {
    if (!root) return [];
    return root.query(selector) as CSElement[];
  }, [root, selector]);
}

/**
 * Хук для получения одного элемента по селектору.
 */
export function useQueryOne(root: CSElement | null, selector: string): CSElement | null {
  const results = useQuery(root, selector);
  return useMemo(() => (results.length > 0 ? results[0] : null), [results]);
}

/**
 * React хук для управления всей системой CSElement
 */
export function useCSElementSystem(options: UseCSElementOptions = {}): UseCSElementResult {
  const {
    root: providedRoot,
    autoConnect = true,
    enableHistory = false,
    enableReactivity = false,
    enablePersistence = false
  } = options;

  const [root, setRoot] = useState<CSElement | null>(providedRoot || null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [stats, setStats] = useState({
    elementCount: 0,
    lastUpdate: null as Date | null
  });

  useEffect(() => {
    const initializeSystem = async () => {
      try {
        setError(null);
        
        let systemRoot = root;
        if (!systemRoot && autoConnect) {
          systemRoot = new CSElement('root');
          setRoot(systemRoot);
        }

        if (systemRoot) {
          setConnected(true);

          if (enableHistory) {
            CSElement.configureHistory({ maxOperations: 100 });
          }
          if (enableReactivity) {
            CSElement.configureReactivity({ debug: true });
          }
          if (enablePersistence) {
            // Persistence configuration
          }

          setStats({
            elementCount: systemRoot.getAllElements().length,
            lastUpdate: new Date()
          });
        }
      } catch (err) {
        setError(err as Error);
      }
    };

    initializeSystem();
  }, [autoConnect, enableHistory, enableReactivity, enablePersistence, root]);

  useEffect(() => {
    if (root) {
      const updateStats = () => {
        setStats({
          elementCount: root.getAllElements().length,
          lastUpdate: new Date()
        });
      };

      updateStats();
      
      root.on('element:added', updateStats);
      root.on('element:removed', updateStats);
      
      return () => {
        root.off('element:added', updateStats);
        root.off('element:removed', updateStats);
      };
    }
    return undefined;
  }, [root]);

  const createElement = useCallback((name: string, data?: Record<string, any>) => {
    if (!root) throw new Error('No root element available');
    
    const element = new CSElement(name);
    
    if (data) {
      Object.entries(data).forEach(async ([key, value]) => {
        try {
          await element.setData(key, value);
        } catch (err) {
          console.error('Error setting data:', err);
        }
      });
    }
    
    return element;
  }, [root]);

  const query = useCallback((selector: string) => {
    if (!root) return [];
    return root.query(selector) as CSElement[];
  }, [root]);

  const exportData = useCallback(() => {
    if (!root) return null;
    
    return root.serialize({
      includeChildren: true,
      includeData: true,
      includeMetadata: true
    });
  }, [root]);

  const importData = useCallback(async (data: any) => {
    if (!data) {
      setError(new Error('Invalid data provided'));
      return;
    }

    try {
      setError(null);
      const importedElement = CSElement.deserialize(data);
      setRoot(importedElement);
      setConnected(true);
    } catch (err) {
      setError(err as Error);
    }
  }, []);

  const connect = useCallback(async () => {
    try {
      setError(null);
      if (!root) {
        const newRoot = new CSElement('root');
        setRoot(newRoot);
      }
      setConnected(true);
    } catch (err) {
      setError(err as Error);
    }
  }, [root]);

  const disconnect = useCallback(() => {
    setConnected(false);
  }, []);

  const clear = useCallback(async () => {
    if (!root) return;
    
    try {
      setError(null);
      const children = [...root.getAllElements()];
      
      for (const child of children) {
        if (child instanceof CSElement) {
          await root.removeElement(child);
        }
      }
      
      setStats({
        elementCount: 0,
        lastUpdate: new Date()
      });
    } catch (err) {
      setError(err as Error);
    }
  }, [root]);

  return {
    root,
    connected,
    error,
    createElement,
    query,
    export: exportData,
    import: importData,
    connect,
    disconnect,
    clear,
    stats
  };
} 